import React, { useState } from "react";
import { Form, Button, Card, Alert } from "react-bootstrap";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSelector from "./LanguageSelector";

const EyeOpen = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeClosed = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface SignInFormProps {
  onSubmit: (identifier: string, password: string) => Promise<void>;
}

const SignInForm: React.FC<SignInFormProps> = ({ onSubmit }) => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { t, dir } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await onSubmit(identifier, password);
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message === "Network Error") {
        setError("Cannot connect to server. Make sure the backend is running");
      } else {
        setError(t("loginFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      className="shadow border-0"
      style={{ borderRadius: "16px" }}
      dir={dir}
    >
      <Card.Body className="p-4">
        <div className="d-flex justify-content-end mb-2">
          <LanguageSelector variant="dark" />
        </div>
        <div className="text-center mb-4">
          <div className="mb-3">
            <img
              src="/logo192.png"
              alt="MATA portal"
              className="mx-auto d-block"
              style={{ width: "70px", height: "70px", objectFit: "contain" }}
            />
          </div>
          <h4 className="mb-1" style={{ fontWeight: "600" }}>
            MATA portal
          </h4>
          <p className="text-muted small mb-0">{t("signIn")}</p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="py-2 small"
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formIdentifier">
            <Form.Label className="small fw-semibold">
              {t("employeeIdOrEmail")}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={t("employeeIdOrEmail")}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={isLoading}
              style={{ padding: "12px", borderRadius: "8px" }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPassword">
            <Form.Label className="small fw-semibold">
              {t("password")}
            </Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                style={{ padding: "12px 44px 12px 12px", borderRadius: "8px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                tabIndex={-1}
                title={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 42,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6c757d",
                  borderRadius: "0 8px 8px 0",
                  padding: 0,
                }}
              >
                {showPassword ? <EyeClosed /> : <EyeOpen />}
              </button>
            </div>
          </Form.Group>

          <div className="d-grid gap-2 mb-3">
            <Button
              variant="primary"
              type="submit"
              size="lg"
              disabled={isLoading}
              style={{
                padding: "12px",
                borderRadius: "8px",
                fontWeight: "600",
              }}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("loading")}
                </>
              ) : (
                t("signIn")
              )}
            </Button>
          </div>

          <div className="text-center">
            <small className="text-muted">{t("forgotPassword")}</small>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SignInForm;
